/* assets/image-link-slider.js */

(() => {
  const FLICKITY_CSS_URL =
    'https://cdn.jsdelivr.net/npm/flickity@2.3.0/dist/flickity.min.css';

  const FLICKITY_JS_URL =
    'https://cdn.jsdelivr.net/npm/flickity@2.3.0/dist/flickity.pkgd.min.js';

  const instances = new WeakMap();

  /**
   * 文字列のtrue / falseをBooleanへ変換
   */
  const toBoolean = (value, fallback = false) => {
    if (value === 'true') return true;
    if (value === 'false') return false;

    return fallback;
  };

  /**
   * FlickityのCSSを一度だけ読み込む
   */
  const loadFlickityCss = () => {
    if (
      document.querySelector(
        'link[data-image-link-slider-flickity-css]'
      )
    ) {
      return;
    }

    const link = document.createElement('link');

    link.rel = 'stylesheet';
    link.href = FLICKITY_CSS_URL;
    link.dataset.imageLinkSliderFlickityCss = '';

    document.head.appendChild(link);
  };

  /**
   * FlickityのJSを一度だけ読み込む
   */
  const loadFlickityScript = () => {
    if (window.Flickity) {
      return Promise.resolve(window.Flickity);
    }

    const existingScript = document.querySelector(
      'script[data-image-link-slider-flickity-js]'
    );

    if (existingScript) {
      return new Promise((resolve, reject) => {
        existingScript.addEventListener(
          'load',
          () => resolve(window.Flickity),
          { once: true }
        );

        existingScript.addEventListener(
          'error',
          () => reject(new Error('Flickityの読み込みに失敗しました。')),
          { once: true }
        );
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');

      script.src = FLICKITY_JS_URL;
      script.async = true;
      script.dataset.imageLinkSliderFlickityJs = '';

      script.addEventListener(
        'load',
        () => resolve(window.Flickity),
        { once: true }
      );

      script.addEventListener(
        'error',
        () => reject(new Error('Flickityの読み込みに失敗しました。')),
        { once: true }
      );

      document.head.appendChild(script);
    });
  };

  /**
   * セクション内の画像が読み込まれた後にサイズを再計算
   */
  const bindImageResize = (root, flickity) => {
    const images = root.querySelectorAll('img');

    images.forEach((image) => {
      if (image.complete) return;

      image.addEventListener(
        'load',
        () => {
          flickity.resize();
        },
        { once: true }
      );
    });
  };

  /**
   * ドラッグ後にリンクが誤作動するのを防ぐ
   */
  const bindDragClickProtection = (carousel, flickity) => {
    let dragged = false;

    flickity.on('dragStart', () => {
      dragged = true;
    });

    flickity.on('settle', () => {
      window.setTimeout(() => {
        dragged = false;
      }, 0);
    });

    carousel.addEventListener(
      'click',
      (event) => {
        if (!dragged) return;

        const link = event.target.closest('a');

        if (!link) return;

        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  };

  /**
   * Flickity初期化
   */
  const initializeSlider = (root) => {
    if (!root || instances.has(root)) return;

    const carousel = root.querySelector('[data-slider-carousel]');

    if (!carousel) return;

    const cells = carousel.querySelectorAll('[data-slider-cell]');

    if (cells.length === 0) return;

    const autoplayValue = carousel.dataset.autoPlay;

    const options = {
      cellSelector: '[data-slider-cell]',
      cellAlign: carousel.dataset.cellAlign || 'left',
      contain: false,
      prevNextButtons: toBoolean(
        carousel.dataset.prevNextButtons,
        true
      ),
      pageDots: toBoolean(carousel.dataset.pageDots, false),
      wrapAround:
        cells.length > 1 &&
        toBoolean(carousel.dataset.wrapAround, true),
      draggable:
        cells.length > 1 &&
        toBoolean(carousel.dataset.draggable, true),
      adaptiveHeight: false,
      imagesLoaded: true,
      percentPosition: true,
      selectedAttraction: 0.025,
      friction: 0.28,
      pauseAutoPlayOnHover: toBoolean(
        carousel.dataset.pauseAutoPlayOnHover,
        true
      ),
      autoPlay:
        autoplayValue !== 'false'
          ? Number.parseInt(autoplayValue, 10)
          : false
    };

    const flickity = new window.Flickity(carousel, options);

    instances.set(root, flickity);

    bindImageResize(root, flickity);
    bindDragClickProtection(carousel, flickity);

    requestAnimationFrame(() => {
      flickity.resize();
    });
  };

  /**
   * Flickityを破棄
   */
  const destroySlider = (root) => {
    const flickity = instances.get(root);

    if (!flickity) return;

    flickity.destroy();
    instances.delete(root);
  };

  /**
   * 指定範囲内のスライダーを初期化
   */
  const initializeAll = async (scope = document) => {
    const sliders = scope.matches?.('[data-image-link-slider]')
      ? [scope]
      : Array.from(
          scope.querySelectorAll('[data-image-link-slider]')
        );

    if (sliders.length === 0) return;

    loadFlickityCss();

    try {
      await loadFlickityScript();

      sliders.forEach((slider) => {
        initializeSlider(slider);
      });
    } catch (error) {
      console.error('[Image link slider]', error);
    }
  };

  /**
   * 通常ページ読み込み
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeAll();
    });
  } else {
    initializeAll();
  }

  /**
   * Shopifyテーマエディター
   */
  document.addEventListener('shopify:section:load', (event) => {
    initializeAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-image-link-slider]')
      ? event.target
      : event.target.querySelector('[data-image-link-slider]');

    if (root) {
      destroySlider(root);
    }
  });

  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target.closest('[data-slider-cell]');
    const root = event.target.closest('[data-image-link-slider]');

    if (!block || !root) return;

    const flickity = instances.get(root);

    if (!flickity) return;

    const index = Array.from(flickity.cells).findIndex(
      (cell) => cell.element === block
    );

    if (index >= 0) {
      flickity.select(index, false, true);
    }
  });
})();